import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Send, Check, X, AlertTriangle } from 'lucide-react';
import { botAPI as api } from '../services/api';

export default function NotificationSettings() {
    const [settings, setSettings] = useState({
        enabled: false, discordWebhookSet: false, discordWebhookMasked: '',
        notifyBuy: true, notifySell: true, notifyStop: true,
        notifyDailySummary: true, notifyErrors: false, notifyBotStart: true,
    });
    const [webhookInput, setWebhookInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = async () => {
        try {
            const res = await api.getNotificationSettings();
            setSettings(res.data);
        } catch (err) { console.error('Failed to load notification settings'); }
    };

    const saveSettings = async (updates) => {
        setSaving(true); setError(null);
        try {
            const payload = { ...updates };
            if (webhookInput && webhookInput !== 'change') payload.discordWebhook = webhookInput;
            await api.updateNotificationSettings(payload);
            setSettings(s => ({ ...s, ...updates }));
            if (webhookInput && webhookInput !== 'change') {
                setSettings(s => ({ ...s, discordWebhookSet: true, discordWebhookMasked: webhookInput.substring(0, 40) + '...' }));
                setWebhookInput('');
            }
        } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
        setSaving(false);
    };

    const testWebhook = async () => {
        setTesting(true); setTestResult(null);
        try {
            const payload = webhookInput && webhookInput !== 'change' ? { discordWebhook: webhookInput } : {};
            const res = await api.testNotification(payload);
            setTestResult(res.data.success ? 'success' : 'fail');
        } catch (err) { setTestResult('fail'); }
        setTesting(false);
        setTimeout(() => setTestResult(null), 5000);
    };

    const toggleSetting = (key) => {
        const newVal = !settings[key];
        setSettings(s => ({ ...s, [key]: newVal }));
        saveSettings({ [key]: newVal });
    };

    const Toggle = ({ label, description, settingKey }) => (
        <div className="flex items-center justify-between py-3 border-b border-border-color/30 last:border-0">
            <div>
                <div className="text-sm font-medium">{label}</div>
                {description && <div className="text-xs text-text-muted">{description}</div>}
            </div>
            <button onClick={() => toggleSetting(settingKey)}
                className={`relative w-12 h-6 rounded-full transition-colors ${settings[settingKey] ? 'bg-accent-green' : 'bg-bg-tertiary'}`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${settings[settingKey] ? 'left-6' : 'left-0.5'}`} />
            </button>
        </div>
    );

    return (
        <div className="card p-6">
            <div className="flex items-center gap-3 mb-6">
                {settings.enabled ? <Bell className="w-6 h-6 text-accent-green" /> : <BellOff className="w-6 h-6 text-text-muted" />}
                <div>
                    <h2 className="text-lg font-bold">Notifications</h2>
                    <p className="text-xs text-text-muted">Get Discord alerts for trades, stops, and daily summaries</p>
                </div>
                <div className="ml-auto">
                    <button onClick={() => saveSettings({ enabled: !settings.enabled })}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                            settings.enabled ? 'bg-accent-green/20 text-accent-green border border-accent-green/30' : 'bg-bg-tertiary text-text-secondary border border-border-color'}`}>
                        {settings.enabled ? '● Enabled' : '○ Disabled'}
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Discord Webhook URL</label>
                {settings.discordWebhookSet && !webhookInput && (
                    <div className="flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4 text-accent-green" />
                        <span className="text-xs text-text-muted font-mono">{settings.discordWebhookMasked}</span>
                        <button onClick={() => setWebhookInput('change')} className="text-xs text-accent-purple hover:underline ml-2">Change</button>
                    </div>
                )}
                {(!settings.discordWebhookSet || webhookInput) && (
                    <div className="flex gap-2">
                        <input type="text" value={webhookInput === 'change' ? '' : webhookInput}
                            onChange={e => setWebhookInput(e.target.value)}
                            placeholder="https://discord.com/api/webhooks/..."
                            className="flex-1 bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm font-mono" />
                        <button onClick={() => saveSettings({})}
                            disabled={saving || !webhookInput || webhookInput === 'change'}
                            className="px-4 py-2 bg-accent-purple text-white rounded-lg text-sm font-medium disabled:opacity-50">
                            {saving ? '...' : 'Save'}
                        </button>
                        <button onClick={testWebhook}
                            disabled={testing || (!webhookInput && !settings.discordWebhookSet)}
                            className="px-4 py-2 bg-bg-tertiary border border-border-color rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                            <Send className="w-3 h-3" />{testing ? '...' : 'Test'}
                        </button>
                    </div>
                )}
                {testResult === 'success' && <div className="flex items-center gap-1 mt-2 text-xs text-accent-green"><Check className="w-3 h-3" /> Test sent! Check Discord.</div>}
                {testResult === 'fail' && <div className="flex items-center gap-1 mt-2 text-xs text-accent-red"><X className="w-3 h-3" /> Failed — check URL</div>}
                {error && <div className="flex items-center gap-1 mt-2 text-xs text-accent-red"><AlertTriangle className="w-3 h-3" /> {error}</div>}
                <p className="text-xs text-text-muted mt-2">Create a webhook: Discord Server Settings → Integrations → Webhooks → New Webhook</p>
            </div>

            <div className="border-t border-border-color pt-4">
                <h3 className="text-sm font-semibold text-text-secondary mb-2">Alert Types</h3>
                <Toggle label="🟢 Buy Alerts" description="When bot opens a new position" settingKey="notifyBuy" />
                <Toggle label="🔴 Sell Alerts" description="When bot closes a position" settingKey="notifySell" />
                <Toggle label="🛑 Stop Loss Alerts" description="When ATR trailing stop triggers" settingKey="notifyStop" />
                <Toggle label="📊 Daily Summary" description="End-of-day P&L and stats" settingKey="notifyDailySummary" />
                <Toggle label="🤖 Bot Start/Stop" description="When the bot starts or stops" settingKey="notifyBotStart" />
                <Toggle label="⚠️ Error Alerts" description="When trades fail or issues occur" settingKey="notifyErrors" />
            </div>
        </div>
    );
}
